import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Printer, RotateCcw } from "lucide-react";

// Label Tom & Jerry No. 121: 75 x 38 mm, 10 label per lembar (2 kolom x 5 baris)
const PER_SHEET = 10;
const CALIB_KEY = "asset_label_calib_v1";
const DEFAULT_CALIB = { top: 5, left: 5, gapX: 6, gapY: 2 };

function loadCalib() {
    try {
        const c = JSON.parse(localStorage.getItem(CALIB_KEY) || "null");
        if (c && typeof c === "object") return { ...DEFAULT_CALIB, ...c };
    } catch {
        // abaikan
    }
    return { ...DEFAULT_CALIB };
}

function chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

function AssetLabel({ asset }) {
    const url = `${window.location.origin}/aset/${asset.id}`;
    const year = asset.first_use_date ? String(asset.first_use_date).slice(0, 4) : "-";
    return (
        <div className="asset-label" data-testid={`asset-label-${asset.code}`}>
            <div className="asset-label-qr">
                <QRCodeSVG value={url} size={256} level="M" style={{ width: "100%", height: "100%" }} />
            </div>
            <div className="asset-label-info">
                <div className="asset-label-name">{asset.name}</div>
                <div className="asset-label-row">No: {asset.code}</div>
                <div className="asset-label-row" data-testid={`asset-label-year-${asset.code}`}>
                    Tahun Pembelian: {year}
                </div>
                <div className="asset-label-row">Lokasi: {asset.location || "-"}</div>
                <div className="asset-label-row">Vendor: {asset.vendor_name || "-"}</div>
                <div className="asset-label-row">Kontak: {asset.vendor_contact || "-"}</div>
            </div>
        </div>
    );
}

function CalibInput({ label, value, onChange, testid }) {
    return (
        <label className="flex items-center gap-1.5 text-xs text-[#C4DAD2]">
            {label}
            <input
                type="number"
                step="0.5"
                value={value}
                data-testid={testid}
                onChange={(e) => onChange(Number(e.target.value) || 0)}
                className="w-16 rounded bg-white/10 border border-white/25 px-2 py-1 text-white text-xs focus:outline-none focus:border-[#6A9C89]"
            />
            <span className="opacity-70">mm</span>
        </label>
    );
}

export default function AssetLabelsPrint() {
    const navigate = useNavigate();
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startSlot, setStartSlot] = useState(1); // 1..10, posisi label pertama di lembar pertama
    const [calib, setCalib] = useState(loadCalib);

    useEffect(() => {
        let ids = [];
        try {
            ids = JSON.parse(sessionStorage.getItem("asset_label_ids") || "[]");
        } catch {
            ids = [];
        }
        api.get("/assets").then((res) => {
            const byId = new Set(ids);
            setAssets(res.data.filter((a) => byId.has(a.id)));
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        localStorage.setItem(CALIB_KEY, JSON.stringify(calib));
    }, [calib]);

    // Slot kosong di awal lembar pertama = posisi label yang sudah terpakai
    const slots = [...Array(startSlot - 1).fill(null), ...assets];
    const sheets = chunk(slots, PER_SHEET);

    return (
        <div
            className="print-root min-h-screen bg-[#4A5E57] print:bg-white"
            data-testid="asset-print-page"
        >
            <style>{`
                .label-sheet {
                    width: 210mm;
                    background: white;
                    display: grid;
                    grid-template-columns: repeat(2, 75mm);
                    grid-auto-rows: 38mm;
                    column-gap: ${calib.gapX}mm;
                    row-gap: ${calib.gapY}mm;
                    padding-top: ${calib.top}mm;
                    padding-left: ${calib.left}mm;
                    padding-bottom: 4mm;
                    margin: 0 auto 16px;
                    box-sizing: border-box;
                }
                .asset-label {
                    width: 75mm;
                    height: 38mm;
                    border: 0.3mm dashed #b6c4be;
                    display: flex;
                    align-items: center;
                    gap: 2.5mm;
                    padding: 2.5mm 3mm;
                    box-sizing: border-box;
                    overflow: hidden;
                    background: white;
                }
                .asset-label.empty-slot {
                    border: 0.3mm dashed #d8e2dd;
                    background: repeating-linear-gradient(45deg, #f4f7f5, #f4f7f5 4px, #eef2ef 4px, #eef2ef 8px);
                }
                .asset-label-qr {
                    width: 27mm;
                    height: 27mm;
                    flex-shrink: 0;
                }
                .asset-label-info {
                    flex: 1;
                    min-width: 0;
                    color: #000;
                    line-height: 1.3;
                }
                .asset-label-name {
                    font-weight: 700;
                    font-size: 11pt;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    margin-bottom: 1.2mm;
                }
                .asset-label-row {
                    font-size: 8.5pt;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .slot-picker {
                    display: grid;
                    grid-template-columns: repeat(2, 26px);
                    grid-auto-rows: 18px;
                    gap: 3px;
                }
                @media print {
                    @page { size: A4 portrait; margin: 0; }
                    html, body {
                        height: auto !important;
                        overflow: visible !important;
                        background: white;
                    }
                    .no-print { display: none !important; }
                    .print-root {
                        min-height: 0 !important;
                        height: auto !important;
                        overflow: visible !important;
                    }
                    .sheets-wrap { padding: 0 !important; }
                    .label-sheet {
                        margin: 0 auto;
                        height: 296mm; /* pas 1 halaman A4: jamin lembar berikutnya pindah halaman */
                        page-break-after: always;
                        break-after: page;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .label-sheet:last-child {
                        page-break-after: auto;
                        break-after: auto;
                    }
                    .asset-label, .asset-label.empty-slot {
                        border-color: transparent;
                        background: white;
                    }
                }
            `}</style>

            <div className="no-print sticky top-0 z-10 bg-[#1A362D] text-white px-6 py-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={() => navigate("/assets")}
                        data-testid="print-back-btn"
                        className="border-white/30 bg-transparent text-white hover:bg-white/10"
                    >
                        <ArrowLeft size={16} className="mr-1" /> Kembali
                    </Button>
                    <div className="flex-1 min-w-0">
                        <div className="font-heading font-semibold">
                            Cetak Label Aset — Tom &amp; Jerry No. 121
                        </div>
                        <div className="text-xs text-[#C4DAD2]">
                            {assets.length} label • mulai slot {startSlot} • {sheets.length} lembar.
                            Cetak dengan skala 100% (Actual Size), tanpa "Fit to page", margin printer 0/None.
                        </div>
                    </div>
                    <Button
                        onClick={() => window.print()}
                        data-testid="print-labels-btn"
                        className="bg-[#6A9C89] hover:bg-[#557E6E] text-white"
                    >
                        <Printer size={16} className="mr-1" /> Cetak
                    </Button>
                </div>

                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="flex items-center gap-2">
                        <div className="text-xs text-[#C4DAD2]">
                            <div className="font-medium text-white mb-1">Mulai dari slot:</div>
                            <div className="opacity-80">
                                Klik posisi label pertama
                                <br />
                                (lewati label yang sudah terpakai)
                            </div>
                        </div>
                        <div className="slot-picker" data-testid="slot-picker">
                            {Array.from({ length: PER_SHEET }, (_, i) => i + 1).map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setStartSlot(n)}
                                    data-testid={`slot-btn-${n}`}
                                    title={`Mulai cetak dari slot ${n}`}
                                    className={`rounded-sm text-[10px] font-medium border transition-colors ${
                                        n < startSlot
                                            ? "bg-white/10 border-white/20 text-white/40 line-through"
                                            : n === startSlot
                                            ? "bg-[#6A9C89] border-[#6A9C89] text-white"
                                            : "bg-white/20 border-white/30 text-white hover:bg-white/30"
                                    }`}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 border-l border-white/20 pl-6">
                        <div className="text-xs font-medium text-white">Kalibrasi presisi:</div>
                        <CalibInput
                            label="Margin atas"
                            value={calib.top}
                            testid="calib-top"
                            onChange={(v) => setCalib({ ...calib, top: v })}
                        />
                        <CalibInput
                            label="Margin kiri"
                            value={calib.left}
                            testid="calib-left"
                            onChange={(v) => setCalib({ ...calib, left: v })}
                        />
                        <CalibInput
                            label="Jarak kolom"
                            value={calib.gapX}
                            testid="calib-gapx"
                            onChange={(v) => setCalib({ ...calib, gapX: v })}
                        />
                        <CalibInput
                            label="Jarak baris"
                            value={calib.gapY}
                            testid="calib-gapy"
                            onChange={(v) => setCalib({ ...calib, gapY: v })}
                        />
                        <button
                            type="button"
                            onClick={() => setCalib({ ...DEFAULT_CALIB })}
                            data-testid="calib-reset"
                            title="Kembalikan kalibrasi standar"
                            className="flex items-center gap-1 text-xs text-[#C4DAD2] hover:text-white"
                        >
                            <RotateCcw size={13} /> Reset
                        </button>
                        <div className="text-[10px] text-[#C4DAD2] opacity-80 max-w-[220px]">
                            Jika hasil cetak bergeser: kolom kanan geser kiri/kanan → ubah "Jarak
                            kolom"; semua bergeser → ubah margin. Nilai tersimpan otomatis.
                        </div>
                    </div>
                </div>
            </div>

            <div className="py-6 sheets-wrap">
                {loading ? (
                    <div className="text-center text-white py-16">Memuat...</div>
                ) : assets.length === 0 ? (
                    <div className="text-center text-white py-16" data-testid="print-empty">
                        Tidak ada aset dipilih. Kembali ke Inventaris Aset dan pilih aset terlebih dahulu.
                    </div>
                ) : (
                    sheets.map((sheet, si) => (
                        <div className="label-sheet" key={si} data-testid={`label-sheet-${si}`}>
                            {sheet.map((a, li) =>
                                a ? (
                                    <AssetLabel key={a.id} asset={a} />
                                ) : (
                                    <div
                                        key={`empty-${si}-${li}`}
                                        className="asset-label empty-slot"
                                        data-testid={`empty-slot-${li + 1}`}
                                    />
                                )
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
